export function slugify(text?: string | null): string {
  if (!text) return '';
  return String(text)
    .toLowerCase()
    .replace(/<[^>]*>/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function getAuthorSlug(name?: string, id?: string): string {
  if (!id) return '';
  const cleanName = slugify(name || 'author');
  return cleanName ? `${cleanName}-${id}` : id;
}

export function getPostSlug(title?: string, authorOrId?: string, idArg?: string): string {
  let author: string | undefined;
  let id = idArg;

  if (!idArg) {
    // Called with 2 arguments: getPostSlug(title, id)
    id = authorOrId;
    author = undefined;
  } else {
    // Called with 3 arguments: getPostSlug(title, author, id)
    author = authorOrId;
  }

  if (!id) return '';
  const cleanTitle = slugify(title || 'piece') || 'piece';
  const cleanAuthor = slugify(author);

  if (cleanAuthor) {
    return `${cleanTitle}-by-${cleanAuthor}-${id}`;
  }
  return `${cleanTitle}-${id}`;
}

export function getPostReadingPath(
  postId: string,
  type?: string | null,
  title?: string | null,
  author?: string | null
): string {
  const cleanType = (type || 'post').toLowerCase();
  const route = cleanType === 'poem'
    ? 'poem'
    : cleanType === 'essay'
      ? 'essay'
      : cleanType === 'article'
        ? 'article'
        : 'post';
  if (author || title) {
    const authorSlug = slugify(author) || 'author';
    const titleSlug = slugify(title) || 'piece';
    return `/${route}/${titleSlug}/${authorSlug}/${postId}`;
  }
  return `/${route}/${postId}`;
}

export interface BookSlugExtra {
  seriesTitle?: string;
  chapterIndex?: number;
}

export function getBookSlug(
  title?: string,
  authorOrId?: string,
  idArg?: string,
  extra?: BookSlugExtra
): string {
  let author: string | undefined;
  let id = idArg;

  if (!idArg) {
    // Called with 2 arguments: getBookSlug(title, id)
    id = authorOrId;
    author = undefined;
  } else {
    // Called with 3 or 4 arguments: getBookSlug(title, author, id, extra)
    author = authorOrId;
  }

  if (!id) return '';
  const cleanTitle = slugify(title || 'book') || 'book';
  const cleanAuthor = slugify(author);

  let base = cleanAuthor ? `${cleanTitle}-by-${cleanAuthor}` : cleanTitle;
  if (extra?.seriesTitle) {
    const cleanSeries = slugify(extra.seriesTitle);
    if (cleanSeries) base += `-${cleanSeries}`;
  }
  if (extra?.chapterIndex !== undefined && extra?.chapterIndex !== null) {
    base += `-chapter-${extra.chapterIndex}`;
  }
  return `${base}-${id}`;
}

const UUID_REGEX = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

export function isUuid(str?: string): boolean {
  if (!str) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str.trim());
}

export function extractAuthorId(slugOrId: string): string {
  if (!slugOrId) return '';
  const trimmed = slugOrId.trim();
  const match = trimmed.match(UUID_REGEX);
  if (match) {
    return match[0];
  }
  const parts = trimmed.split('/');
  const lastSegment = parts[parts.length - 1] || trimmed;
  const subParts = lastSegment.split('-');
  const lastPart = subParts[subParts.length - 1];
  if (lastPart && lastPart.length >= 6) {
    return lastPart;
  }
  return lastSegment;
}

export const extractEntityId = extractAuthorId;
